// MODMAP location switching. Two triggers select a location — a map point
// (`[data-modmap-point]`, desktop) and a mobile `<select>`
// (`[data-modmap-select]`, < 768px). Both route through `applyLocation`, which
// fetches that location's `.ajax` fragment (`_ajax/modmaplocation_full` → the
// location `content` view) and swaps it into the panel (`[data-modmap-content]`)
// while syncing the active map point and the select value. The current panel
// cross-fades out, the new one fades in with its events staggering up; the
// in-flight feedback reuses the global ASwap navigation loading indicator
// (`navLoading`). Delegated at the document so it survives ASwap page swaps.
import { gsap } from 'gsap';
import { navLoadingStart, navLoadingEnd } from './navLoading';

// Latest-selection wins: a slow response superseded by a newer pick is
// discarded instead of clobbering the panel.
let reqToken = 0;

export const initModmap = (): void => {
  document.addEventListener('click', onPointClick);
  document.addEventListener('change', onSelectChange);
};

const prefersReducedMotion = (): boolean =>
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const onPointClick = (e: MouseEvent): void => {
  const point = (e.target as Element).closest<HTMLButtonElement>('[data-modmap-point]');
  if (!point) return;
  e.preventDefault();
  if (point.getAttribute('aria-pressed') === 'true') return; // already open
  applyLocation(point.closest<HTMLElement>('.modmap-main'), point.dataset.id, point.dataset.url);
};

const onSelectChange = (e: Event): void => {
  const select = (e.target as Element).closest<HTMLSelectElement>('[data-modmap-select]');
  if (!select) return;
  const opt = select.selectedOptions[0];
  applyLocation(select.closest<HTMLElement>('.modmap-main'), opt?.value, opt?.dataset.url);
};

/** Load `id`/`url` into the module: sync point + select, then animate the swap. */
const applyLocation = (
  root: HTMLElement | null,
  id: string | undefined,
  url: string | undefined,
): void => {
  if (!root || !id || !url) return;
  const wrapper = root.querySelector<HTMLElement>('[data-modmap-content]');
  if (!wrapper) return;

  syncActive(root, id);

  const token = ++reqToken;
  navLoadingStart();

  const fadeOut = animateOut(wrapper.firstElementChild as HTMLElement | null);

  fetch(url)
    .then((r) => r.text())
    .then(async (html) => {
      await fadeOut;
      if (token !== reqToken) return; // a newer selection took over
      navLoadingEnd();
      wrapper.innerHTML = html;
      animateIn(wrapper);
    })
    .catch(() => {
      if (token === reqToken) navLoadingEnd();
    });
};

/** Reflect the active location on both navigations (map points + the select). */
const syncActive = (root: HTMLElement, id: string): void => {
  root.querySelectorAll<HTMLElement>('[data-modmap-point]').forEach((p) => {
    const on = p.dataset.id === id;
    p.classList.toggle('is-active', on);
    p.setAttribute('aria-pressed', on ? 'true' : 'false');
  });
  const select = root.querySelector<HTMLSelectElement>('[data-modmap-select]');
  if (select && select.value !== id) select.value = id;
};

/** Ease the outgoing panel up and out. Resolves immediately under reduced motion. */
const animateOut = (el: HTMLElement | null): Promise<void> => {
  if (!el || prefersReducedMotion()) return Promise.resolve();
  return new Promise((resolve) => {
    gsap.to(el, { opacity: 0, y: -12, duration: 0.25, ease: 'power2.in', onComplete: resolve });
  });
};

/** Reveal the swapped-in panel: header then each event rises and fades in. */
const animateIn = (wrapper: HTMLElement): void => {
  const panel = wrapper.querySelector<HTMLElement>('[data-modmap-panel]');
  if (!panel) return;
  gsap.set(panel, { opacity: 1, y: 0 });
  if (prefersReducedMotion()) return;

  const header = panel.querySelector<HTMLElement>('.modmaplocation-content-header');
  const events = Array.from(panel.querySelectorAll<HTMLElement>('[data-modmap-event]'));
  const targets = [header, ...events].filter((el): el is HTMLElement => el !== null);
  if (!targets.length) return;

  gsap.from(targets, { opacity: 0, y: 20, duration: 0.5, stagger: 0.08, ease: 'power3.out' });
};
